import React, { useCallback } from 'react';
import type { CustomPage } from '../types/page';
import { t, Trans } from '@lingui/macro';
import { PageTitle } from '../helpers/PageTitle';
import { useAuth0 } from '@auth0/auth0-react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { Button } from '../components/Button/Button';
import { GoogleIcon } from '../components/GoogleIcon/GoogleIcon';
import { MicrosoftIcon } from '../components/MicrosoftIcon/MicrosoftIcon';
import { BackgroundWave } from '../components/BackgroundWave/BackgroundWave';
import { Carousel } from '../components/Carousel/Carousel';
import { LoginFooter } from '../components/LoginFooter/LoginFooter';
import { useRouter } from 'next/router';
import type { AuthorizationParams } from '@auth0/auth0-spa-js';
import { AUTH_INVITATION_CODE_KEY } from '../constants/appConstants';
import { media } from '../mixins/media';

/**
 * Scoped to this page: the login screen is the only surface using the Evidend brand
 * typeface and the cream/maroon/red palette, so the face is only fetched here and the
 * tokens only exist below `LoginPage`. Nothing else in the app shifts.
 */
const LoginGlobalStyles = createGlobalStyle`
  /* NT Somic - Variable font (weight axis 400–700) */
  @font-face {
    font-family: 'NT Somic';
    font-display: swap;
    src:
      url('/fonts/NTSomic/NT_Somic-VF.ttf') format('truetype-variations'), /* variable: modern browsers */
      url('/fonts/NTSomic/NTSomic-Regular.woff2') format('woff2'); /* static fallback: no variable-font support */
    font-weight: 400 700;
    font-style: normal;
  }

  /**
   * body is a fixed 100vh scroll container (see GlobalSiteStyling), so the page's own
   * background stops at the wrapper edge — without this the overscroll area stays white.
   */
  body {
    background-color: #f9f2ea;
  }
`;

const LoginPage = styled.section`
  --login-bg: #f9f2ea;
  --login-surface: #ffffff;
  --login-ink: #490000;
  --login-ink-soft: #8f6256;
  --login-primary: #dc1e29;
  --login-primary-hover: #e4444d;
  --login-primary-strong: #8d141b;
  --login-dash-idle: #f9d2d5;
  --login-link-hover: #b31a22;
  --login-radius-sm: 1rem;
  --login-radius-pill: 6.25rem;

  /**
   * Nothing in the app sets a global border-box reset, so without this the padding below is
   * added on top of the 100dvh and the body scrolls by exactly that much.
   */
  box-sizing: border-box;

  font-family: 'NT Somic', sans-serif;
  background-color: var(--login-bg);

  /**
   * Exactly one viewport, never more: the two-column layout is meant to be taken in at a
   * glance, so the page does not scroll and the footer sits on the bottom edge. No bottom
   * padding — the footer's own min-height is the breathing room.
   */
  height: 100dvh;
  padding: 1.5rem 1.5rem 0 1.5rem;

  display: grid;
  grid-template-columns: 1fr 1fr;
  /**
   * minmax(0, 1fr) rather than 1fr: a 1fr track keeps an auto minimum, so on a short window
   * the content row would grow past the fixed height and push the footer out of the clip.
   */
  grid-template-rows: minmax(0, 1fr) auto;

  /** Anchors the absolutely-positioned background wave to the full padding box. */
  position: relative;
  overflow: hidden;

  /** Below this the two columns are too narrow for the slide copy, so they stack. */
  ${media.lessThan('m')`
    /**
     * Stacked, the copy plus the card plus the footer are genuinely taller than a phone
     * screen, so here the box grows and the document scrolls — a fixed height would put the
     * sign-in buttons behind the overflow: hidden below with no way to reach them.
     */
    height: auto;
    min-height: 100dvh;

    grid-template-columns: 1fr;

    /**
     * The card row takes the slack rather than all three rows sharing it — with three
     * auto rows the grid's default stretch would inflate the footer row too and leave
     * the footer text floating in a tall empty band instead of sitting at the bottom.
     */
    grid-template-rows: auto 1fr auto;
    padding-inline: clamp(1rem, 4vw, 1.5rem);
  `}
`;

/** Shared so the card can offset its heading past the same wordmark the slides clear. */
const LOGO_HEIGHT = 32;
const logoGutter = 'clamp(1rem, 6dvh, 3rem)';

/**
 * The space the carousel keeps above its first slide, restated here rather than left inside the
 * component: the card opposite has to clear the same amount for the two headings to start on one
 * line, and a value only the carousel knows about cannot be mirrored.
 */
const carouselLead = '1.5rem';

/**
 * The offset above the first heading. Rigid, and used by both columns: the two headings only
 * start on the same line if neither side can give here, so a short window takes its slack from
 * the gap above the dash stepper instead (see the Carousel) rather than from this whitespace.
 */
const headSpacer = css`
  content: '';
  flex: none;
  height: clamp(2rem, 15dvh, 12rem);
`;

/**
 * Both blocks need a stacking context: a positioned element paints above its
 * non-positioned siblings regardless of DOM order, so without this the wave would sit on
 * top of the white card instead of behind it.
 */
const CarouselBlock = styled.section`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;

  &::before {
    ${headSpacer};
  }

  /**
   * Once even the collapsed stepper gap is not enough, the marketing column is still the only
   * thing allowed to lose out: min-height: 0 lets it shrink past its content (grid items
   * refuse to by default) and it clips itself, leaving the card and footer untouched.
   */
  min-height: 0;
  overflow: hidden;

  ${media.lessThan('m')`
    &::before {
      height: clamp(1.5rem, 6dvh, 4rem);
    }

    max-width: 90dvw;
  `}
`;

/**
 * The logo and the carousel share a width so the wordmark lines up with the left edge of
 * the slide copy — the block centres its children, so without an explicit width the logo
 * would shrink to the image and sit in the middle.
 */
const columnWidth = css`
  width: clamp(26rem, 30dvw, 44rem);

  ${media.lessThan('m')`
    width: min(100%, 34rem);
  `}
`;

/**
 * One typographic step for both headings: the slide titles and the card title sit side by side,
 * so the scale lives in one place. The dvh term especially has to stay shared — otherwise the two
 * columns disagree about how much a short window is allowed to shrink them.
 */
const titleType = css`
  font-weight: 700;
  color: var(--login-ink);
  /**
   * The design's fixed 4rem/1.5rem pair is unreadable once the column narrows, so both steps
   * are scaled against the viewport instead. The dvh term is what keeps a wide-but-short
   * window on one screen: without it the copy alone outgrows the page and forces a scroll.
   */
  font-size: clamp(2rem, min(4.5dvw, 8dvh), 4rem);
  line-height: 1.08;
`;

const Logo = styled.div`
  ${columnWidth};
  flex: none;
  margin-block-end: ${logoGutter};

  img {
    display: block;
  }
`;

const SlideCarousel = styled(Carousel)`
  ${columnWidth};

  /**
   * The one thing in this column allowed to give: on a short window the carousel collapses the
   * gap above its dash stepper, which keeps everything above — the logo and the slide heading,
   * which has a heading opposite it to line up with — exactly where it is.
   */
  flex: 0 1 auto;
  min-height: 0;

  /** Its own margin plus padding come to the same total; this just names them in one place. */
  margin-block-start: 0;
  padding-block-start: ${carouselLead};
`;

const Slide = styled.div`
  display: block;
  width: 100%;
  text-align: start;
`;

const SlideTitle = styled.h2`
  margin: 0;
  ${titleType};
`;

const SlideBody = styled.p`
  margin: clamp(1.5rem, 4dvh, 3rem) 0 0 0;
  font-weight: 400;
  color: var(--login-ink-soft);
  font-size: clamp(1rem, min(1.6dvw, 3dvh), 1.5rem);
  /** Ratio rather than the design's flat 2rem, which it still resolves to at the 1.5rem max. */
  line-height: 1.35;
`;

const ContentBlock = styled.section`
  position: relative;
  z-index: 1;
  background-color: var(--login-surface);
  border-radius: var(--login-radius-sm);

  display: flex;
  flex-direction: column;
  align-items: center;

  /**
   * Not centred: the heading lines up with the slide heading opposite it, so the card clears
   * the same spacer the marketing column does plus the logo row that column carries above its
   * own heading. The spacer is shared rather than a flat length so both sides give way together
   * on a short window.
   */
  justify-content: flex-start;

  &::before {
    ${headSpacer};
    margin-block-end: calc(${LOGO_HEIGHT}px + ${logoGutter} + ${carouselLead});
  }

  /** Last resort on an extremely short window: the card scrolls, the page still does not. */
  min-height: 0;
  overflow-y: auto;

  ${media.lessThan('m')`
    /** Stacked there is no column to line up with, so the card just centres its content again. */
    justify-content: center;

    &::before {
      content: none;
    }

    margin-block: 1.5rem;
    padding: 2rem clamp(1rem, 4vw, 2rem);
  `}
`;

const ContentCard = styled.div`
  /**
   * Shrink-to-fit by default, which on the stacked layout would size the whole block to
   * the widest nowrap button label. An explicit width resolves it against the card
   * instead, so the buttons fill the card rather than the reverse.
   */
  ${media.lessThan('m')`
    width: 100%;
    max-width: 22rem;
  `}
`;

const CardTitle = styled.h4`
  margin: 0 0 1.5rem 0;
  ${titleType};
  text-align: center;

  /**
   * The card is only as wide as its widest child, so a title that wrapped would be the thing
   * deciding the card's width. At the shared scale the text tracks its own column — the 4.5dvw
   * step works out to roughly 47dvw of text in a 50dvw column — so one line holds all the way
   * down to the breakpoint.
   */
  white-space: nowrap;

  ${media.lessThan('m')`
    /**
     * Stacked, the card is capped at 22rem while the shared scale keeps growing with the
     * viewport: from around 840px wide the nowrap title would run out under the page's clip.
     * Held at the size it has today instead, which that cap fits.
     *
     * Under ~364px the cap stops being what binds — the viewport itself is narrower than the
     * text at 2rem — so below that the step gives way rather than the line breaking.
     */
    font-size: min(2rem, 8vw);
  `}
`;

const Actions = styled.div`
  /**
   * The buttons are white-space: nowrap, so the column has to stay wide enough for the
   * longest label ("Inloggen zonder wachtwoord") or they overflow the card. The 17.5rem
   * floor is that label plus its padding.
   *
   * A width rather than a min-width: the title above is now the wider of the two, so the stack
   * would otherwise be stretched out to it. This keeps the buttons at their own size and centres
   * them under the heading.
   */
  width: clamp(17.5rem, 22dvw, 24rem);
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  row-gap: 0.625rem;

  ${media.lessThan('m')`
    width: 100%;
  `}
`;

/**
 * `&&` rather than a single class: the base Button styles and these overrides are both
 * one class deep, so without the extra specificity the winner would depend on stylesheet
 * injection order.
 */
const LoginButton = styled(Button)`
  && {
    height: 48px;
    min-width: 80px;
    padding-inline: 16px;
    border-radius: var(--login-radius-pill);
    font-family: 'NT Somic', sans-serif;
    font-size: 1rem;
    line-height: 1.5;
    font-weight: 600;
    column-gap: 8px;
    white-space: nowrap;
    justify-content: center;
    transition: all 0.3s;

    &:active {
      transform: scale(0.9);
    }

    /**
     * The base Button draws its focus state as an inset Titan box-shadow, which reads as a
     * blue ring on the brand red. Swapped for the same outline the dash stepper uses, so
     * every focusable thing on the page gets one treatment.
     */
    &:focus {
      box-shadow: none;
    }

    &:focus-visible {
      outline: 2px solid var(--login-primary-strong);
      outline-offset: 2px;
    }

    /** The label span carries its own icon gutter; column-gap already spaces them. */
    > span {
      padding: 0;
    }
  }
`;

const OutlineButton = styled(LoginButton)`
  && {
    background-color: transparent;
    color: #6f7d95;
    border: 1px solid #cdd3df;
    box-shadow: none;

    &:hover {
      color: #5a677e;
      background-color: #e6e9ef;
      border-color: transparent;
    }
  }
`;

const PrimaryButton = styled(LoginButton)`
  && {
    background-color: var(--login-primary);
    color: #ffffff;
    border: 0;
    box-shadow: none;

    &:hover {
      background-color: var(--login-primary-hover);
      box-shadow: none;
    }
  }
`;

const StyledLoginFooter = styled(LoginFooter)`
  grid-column: 1 / -1;

  /**
   * Same stacking-context reason as the two blocks above: the background wave is
   * positioned, so without this it would paint over the footer.
   */
  position: relative;
  z-index: 1;
`;

interface IAuthParams extends AuthorizationParams {
  connection: 'email' | 'google-oauth2' | 'windowslive';
}

const Login: CustomPage = () => {
  const { loginWithRedirect } = useAuth0();
  const router = useRouter();
  const invitationCode = router.query[AUTH_INVITATION_CODE_KEY];

  const authorize = useCallback(
    (authParams?: IAuthParams) => {
      loginWithRedirect(
        authParams
          ? {
              authorizationParams: {
                connection: authParams.connection,
                ...(invitationCode && {
                  'ext-invitation_code': invitationCode,
                }),
              },
            }
          : undefined,
      );
    },
    [loginWithRedirect, invitationCode],
  );

  const isDevelop =
    location.hostname.includes('localhost') || location.hostname.includes('develop');

  return (
    <>
      <PageTitle title={t`Login`} />
      <LoginGlobalStyles />

      <LoginPage>
        <BackgroundWave />

        <CarouselBlock>
          <Logo>
            <img alt="Evidend" height={LOGO_HEIGHT} src="/images/logo_text.svg" width={145} />
          </Logo>

          <SlideCarousel>
            <Slide>
              <SlideTitle>
                <Trans>Stay in control with ease</Trans>
              </SlideTitle>
              <SlideBody>
                <Trans>
                  Ensure your shareholder registers are always accurate and accessible. Monitor,
                  review and sign off on all changes entirely online.
                </Trans>
              </SlideBody>
            </Slide>

            <Slide>
              <SlideTitle>
                <Trans>Always transparent pricing</Trans>
              </SlideTitle>
              <SlideBody>
                <Trans>
                  A new Evidend register costs a one-off fee of EUR 25. In addition, you pay a small
                  fee for every change to the register.
                </Trans>
              </SlideBody>
            </Slide>

            <Slide>
              <SlideTitle>
                <Trans>Subscribe for premium features</Trans>
              </SlideTitle>
              <SlideBody>
                <Trans>
                  Both notary practices and companies can take out a subscription for additional
                  features such as the group chart editor and UBO reports.
                </Trans>
              </SlideBody>
            </Slide>

            <Slide>
              <SlideTitle>
                <Trans>Already 10,000+ registers online</Trans>
              </SlideTitle>
              <SlideBody>
                <Trans>
                  Thousands of companies relied on Evidend for years. You can manage an Evidend
                  register yourself or have the notary do it for you.", "login_slide_t5": "Built by
                  and for notary practices.
                </Trans>
              </SlideBody>
            </Slide>

            <Slide>
              <SlideTitle>
                <Trans>Built by and for notary practices</Trans>
              </SlideTitle>
              <SlideBody>
                <Trans>
                  Say goodbye to paper registers and start using a faster, safer and better solution
                  today. Satisfied customers guaranteed.
                </Trans>
              </SlideBody>
            </Slide>
          </SlideCarousel>
        </CarouselBlock>

        <ContentBlock>
          <ContentCard>
            <CardTitle>
              <Trans>Inloggen bij Evidend</Trans>
            </CardTitle>

            <Actions>
              <OutlineButton
                isFullWidth
                iconLeft={<GoogleIcon />}
                variant="white-bordered"
                onClick={() => authorize({ connection: 'google-oauth2' })}
              >
                <Trans>Inloggen met Google</Trans>
              </OutlineButton>

              <OutlineButton
                isFullWidth
                iconLeft={<MicrosoftIcon />}
                variant="white-bordered"
                onClick={() => authorize({ connection: 'windowslive' })}
              >
                <Trans>Inloggen met Microsoft</Trans>
              </OutlineButton>

              {isDevelop && (
                <PrimaryButton isFullWidth variant="primary" onClick={() => authorize()}>
                  <Trans>Inloggen</Trans>
                </PrimaryButton>
              )}

              <PrimaryButton
                isFullWidth
                variant="primary"
                onClick={() => authorize({ connection: 'email' })}
              >
                <Trans>Inloggen zonder wachtwoord</Trans>
              </PrimaryButton>
            </Actions>
          </ContentCard>
        </ContentBlock>

        <StyledLoginFooter />
      </LoginPage>
    </>
  );
};

Login.allowUnauthorized = true;

export default Login;
